import type { UIEvent } from "react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import type { UiColorPalette } from "../utils/UIContext";
import { type UiPaletteKey, resolveUiPalette } from "../utils/uiPalette";
import type { CardFooterProps } from "./Card.types";

const Footer = styled.div<{ $color: UiColorPalette }>`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  margin: 0;
  padding: 1em;
  max-height: 50px;
  box-sizing: border-box;
  text-align: center;
  font-weight: 600;
  text-transform: uppercase;
  text-decoration: none;
  border-radius: 0 0 0.3rem 0.3rem;
  background-color: ${({ $color }) => $color.solid};
  color: ${({ $color }) => $color.onSolid};

  &.is-clickable {
    cursor: pointer;

    &:hover,
    &:focus-visible {
      background-color: ${({ $color }) => $color.strong};
    }
  }
`;

const CardFooterComponent = (props: CardFooterProps) => {
  const {
    color = "primary",
    children,
    className,
    style,
    onClick,
    tabIndex,
  } = props;
  const theme = useTheme();
  const palette = resolveUiPalette(theme, color as UiPaletteKey);
  const clickable = !!onClick;

  return (
    <Footer
      className={cx(clickable && "is-clickable", className)}
      style={style}
      onClick={onClick}
      onKeyDown={handleClickForKeyboard(
        onClick as ((e: UIEvent) => void) | undefined,
      )}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? (tabIndex ?? 0) : tabIndex}
      $color={palette}
    >
      {children}
    </Footer>
  );
};

export const CardFooter = styled(CardFooterComponent)``;
